import type { DrawerProps } from '/#/components/base/drawer'
import type { ShallowRef } from 'vue'
import type {
  RouteLocation,
  RouteLocationNamedRaw,
  RouteLocationPathRaw,
} from 'vue-router'

import isFunction from 'lodash-es/isFunction'
import isString from 'lodash-es/isString'
import isUndefined from 'lodash-es/isUndefined'
import last from 'lodash-es/last'
import { defineStore } from 'pinia'

import { store } from '/@/store'
import { useRouter } from '/@/store/router'
type RouterDrawerTypes = string | RouteLocationPathRaw | RouteLocationNamedRaw

export const useRouterDrawerStore = defineStore('routerDrawer', () => {
  const isNavigating = ref(false)

  const router = useRouter()
  /**
   * @name drawerName
   * @description Drawer title name. It will be shown in the drawer header.
   * @type {string}
   * @default ''
   * @example
   * setRouteName('Create User') => <Drawer title="Create User" />
   */
  const drawerName = ref('')

  /**
   * @name drawerPropList
   * @description Drawer props. It will be used for the drawer component.
   * @type {DrawerProps}
   * @default {}
   * @example
   * setDrawerProps({ title: 'Create User', size: 'sm' }) => <Drawer title="Create User" size="sm" />
   */
  const drawerPropList: Ref<DrawerProps> = ref({})

  /**
   * @name isDrawerOpen
   * @description Drawer visibility status. It will be used for the drawer component.
   * @type {boolean}
   * @default false
   * @example
   * openDrawer() => <Drawer open />
   */
  const isDrawerOpen: Ref<boolean> = ref(false)

  /**
   * @name nextRoute
   * @description Next route to be opened in the drawer.
   * It is used for the new route.
   * this variable also represents the page open in the drawer on the route side. because the background view works separately.
   * so this variable is needed to show two different pages. this variable carries the active route information.
   * @type {RouteLocation | undefined}
   * @default undefined
   */
  const nextRoute: Ref<RouteLocation | undefined> = shallowRef(undefined)
  /**
   * @name drawerComponent
   * @description Drawer component. It will be used for the drawer component.
   * @default undefined
   * @type {ShallowRef<any>}
   * @example
   * drawerComponent.value = defineAsyncComponent(() => import('/@/views/demo/index.vue'))
   */
  const drawerComponent: ShallowRef<any> = shallowRef()
  /**
   * @name backgroundView
   * @description this is needed to show the page in the background.
   * in navbar-layout this view is shown to the user. it is not active, it is only used for display.
   * @type {Ref<any>}
   */
  const backgroundView: Ref<any> = ref()
  /**
   * @name middleViews
   * @description It is necessary for the drawer to work with more than one.
   * openRouteWithDrawer function adds new pages to this variable. according to the data in it according to the state of closure.
   * @type {Ref<RouteLocation[]>}
   * @default []
   * @example
   * middleViews.value = [{ name: 'index', path: '/', fullPath: '/' }]
   * middleViews.value = [{ name: 'index', path: '/', fullPath: '/' }, { name: 'issues', path: '/issues', fullPath: '/issues' }]
   */
  const middleViews: Ref<RouteLocation[]> = ref([])

  /**
   * @method openDrawer
   * @description Open the drawer. It sets the isDrawerOpen variable to true.
   * @example
   * openDrawer() => <Drawer open />
   */
  function openDrawer() {
    isDrawerOpen.value = true
  }

  /**
   * @method closeDrawer
   * @description Close the drawer. It sets the isDrawerOpen variable to false.
   * @example
   * closeDrawer() => <Drawer />
   */
  function closeDrawer() {
    isDrawerOpen.value = false
  }

  /**
   * @method setRouteName
   * @description Set the drawer title name. It sets the drawerName variable.
   * @param value {drawerName}
   * @example
   * setRouteName('Create User') => <Drawer title="Create User" />
   */
  function setRouteName(value: string) {
    drawerName.value = value
  }

  /**
   * @method setDrawerProps
   * @description Set the drawer props. It sets the drawerPropList variable.
   * @param value {drawerIconName}
   * @example
   * setDrawerProps({ title: 'Create User', size: 'sm' }) => <Drawer title="Create User" size="sm" />
   */
  function setDrawerProps(props: DrawerProps) {
    drawerPropList.value = props
  }

  /**
   * @method setNextRoute
   * @description Set the next route to be opened in the drawer
   * @param value
   */
  function setNextRoute(value: RouteLocation | undefined) {
    nextRoute.value = value
  }

  /**
   * @method setBackgroundView
   * @description Set backgroundView. If any route opened in drawer, set as previous route.
   * @param value
   */
  function setBackgroundView(value: String | undefined) {
    backgroundView.value = value
  }

  /**
   * @method setLastComponent
   * @description Resolve last component from to. It resolves last component from to, And we open in drawer.
   * @param resolved
   */
  function setLastComponent(resolved: RouteLocation) {
    const lastComponent = last(resolved.matched)?.components?.default
    if (!lastComponent) return

    if (isFunction(lastComponent)) {
      // @ts-ignore
      // It means, target component never loaded yet. Load async.
      // TODO: update async loader component
      drawerComponent.value = defineAsyncComponent(lastComponent)
    } else {
      // It means, target component loaded. Load sync.
      drawerComponent.value = lastComponent
    }
  }

  /**
   * @method closeRouteInDrawer
   * @description Closes a route in the drawer.
   * If multiple routes are opened in the drawer, it removes the specified route from the middleViews array and updates the current route.
   * If only one route is opened, it closes the drawer and resets the next route.
   * -1 means close the all routes in the drawer.
   * @param index
   */
  function closeRouteInDrawer(index: number) {
    // If the middleViews array is not empty, it means that multiple routes are opened in the drawer.
    if (middleViews.value.length > 0 && index !== -1) {
      // Hold the last view in the middleViews array. This is using for the current route.
      const lastView = last(middleViews.value) as RouteLocation

      // Get the indexed view from the middleViews array. This is using for the target route.
      const indexedView = middleViews.value[index]

      // If the route to be closed is not in the middleviews, this is the current route. In this case, we take the last element in the middleviews and assign it to the current route.
      if (isUndefined(indexedView)) {
        middleViews.value.pop()
        setNextRoute(lastView)
        setLastComponent(lastView)
        router.replace(lastView)
        return
      }
      // If the route to be closed is in the middleviews, we remove the route from the middleviews array and assign the indexed route to the current route.
      middleViews.value.splice(index, 1)
      return
    }

    // If the middleViews array is empty, it means that the sinle route is opened in the drawer.
    router.replace(backgroundView.value)
    closeDrawer()
    setNextRoute(undefined)
  }

  function closeAllRoutesInDrawer() {
    closeRouteInDrawer(-1)
    middleViews.value = []
  }

  /**
   * @method openRouteInDrawer
   * @description Opens a route in the drawer.
   * If the drawer is already open, it resolves the route and updates the current route. and finally push the route in middleViews.
   * If the drawer is not open, it sets the drawer properties, opens the drawer, and sets the background view.
   * @param to - Which router will be opened.
   */
  async function openRouteInDrawer(to: RouterDrawerTypes, drawerProps?: DrawerProps) {
    drawerPropList.value = {}
    // It means any route already opened with drawer.
    // We don't want that this operation works recursively.
    // So We restore opened route and close the drawer.
    if (isString(to)) to = { name: to }

    if (isDrawerOpen.value) {
      // It means we can open the route in the drawer with multiple views.
      const resolved = router.resolve(to)

      setLastComponent(resolved)
      setNextRoute(resolved)

      middleViews.value.push(router.currentRoute.value)
    } else {
      if (drawerProps) setDrawerProps(drawerProps)

      // It means we can open the route in the drawer.
      openDrawer()
      setBackgroundView(router.currentRoute.value.fullPath)

      const resolved = router.resolve(to)

      setLastComponent(resolved)
      setNextRoute(resolved)
    }

    await router.push(to)
  }

  /**
   * @method closeDrawerOrOpenRoute
   * @description this method closes the drawer if the route is opened at drawer
   * or the route redirects to the desired location
   * @param route desired location
   */
  async function closeDrawerOrOpenRoute(route: RouterDrawerTypes | string) {
    if (isString(route)) route = { name: route }
    if (isDrawerOpen.value) {
      await router.push(route)
    } else {
      closeRouteInDrawer(-1)
    }
  }

  watch(backgroundView, () => {
    middleViews.value = []
  })

  return {
    isNavigating,
    nextRoute,
    drawerName,
    drawerPropList,
    isDrawerOpen,
    drawerComponent,
    backgroundView,
    middleViews,
    openDrawer,
    openRouteInDrawer,
    closeDrawer,
    closeRouteInDrawer,
    setRouteName,
    setNextRoute,
    setBackgroundView,
    setLastComponent,
    closeDrawerOrOpenRoute,
    closeAllRoutesInDrawer,
  } as const
})

// Need to be used outside the setup
export function useRouterDrawerStoreWithout() {
  return useRouterDrawerStore(store)
}
